import { Component, h, Host, Element, Prop, State, Watch } from "@stencil/core";

const mediaQueryString = "(min-width: 700px)";

/**
 * @slot title
 * @slot copy - Slot for the long text on the detail layer (`<p>` or `<div>`)
 */
@Component({
    tag: "ux-iws-nb-application-example-tile",
    styleUrl: "nb-application-example-tile.scss",
    shadow: true,
})
export class NbApplicationExampleTile {
    @Element() host: HTMLUxIwsNbApplicationExampleTileElement;

    /** label for the open-button */
    @Prop() openButtonLabel: string = "";
    /** poster image for the video */
    @Prop() videoPoster: string = "";
    /** type of the video */
    @Prop() videoType: string = "video/mp4";
    /** video source */
    @Prop() video: string;
    /** video to be shown inside the modal */
    @Prop() modalVideo: string;
    /** image to be shown inside the modal (instead of modalVideo) */
    @Prop() modalImage: string;
    /** headline for the challenge section */
    @Prop() challengeHeadline: string;
    /** headline for the solution section */
    @Prop() solutionHeadline: string;
    /** link */
    @Prop() link: string;
    /** link label */
    @Prop() linkLabel: string;

    private videoElement: HTMLVideoElement;
    private modalVideoElement: HTMLVideoElement;
    private mediaQuery: MediaQueryList = window.matchMedia(mediaQueryString);
    private prefersReducedMotion: boolean = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    @State() isOpened: boolean = false;
    @State() isMobile: boolean = !window.matchMedia(mediaQueryString).matches;
    @State() isHovered: boolean = false;
    @State() isVideoPlaying: boolean = false;

    private mediaQueryListener = (e: MediaQueryListEvent) => {
        this.isMobile = !e.matches;
    };

    connectedCallback() {
        this.mediaQuery.addEventListener("change", this.mediaQueryListener);
        this.handleVideoPlayback();
    }

    disconnectedCallback() {
        this.mediaQuery?.removeEventListener("change", this.mediaQueryListener);
    }

    @Watch("isOpened")
    @Watch("isMobile")
    @Watch("isHovered")
    handleVideoPlayback() {
        this.isVideoPlaying =
            (this.isHovered || (this.isMobile && !this.isOpened)) &&
            !this.prefersReducedMotion;
    }

    @Watch("isVideoPlaying")
    handleVideoElement() {
        const { videoElement } = this;
        if (!videoElement) {
            return;
        }
        if (this.isVideoPlaying) {
            videoElement.play();
        } else {
            videoElement.pause();
        }
    }

    private renderModalMedia(breakpoint: "desktop" | "mobile") {
        const backgroundImage = !this.modalVideo
            ? `url(${this.modalImage})`
            : undefined;
        return (
            <div
                class={`modal-media modal-media--${breakpoint}`}
                style={{ backgroundImage }}
            >
                {this.modalVideo && (
                    <ux-video>
                        <video
                            ref={(el) => (this.modalVideoElement = el)}
                            preload={this.isOpened ? "auto" : "none"}
                            muted={!this.isOpened}
                            controls
                        >
                            <source
                                src={this.modalVideo}
                                type={this.videoType}
                            />
                        </video>
                    </ux-video>
                )}
            </div>
        );
    }

    private renderLinkButton(breakpoint: "desktop" | "mobile") {
        if (!this.link) {
            return;
        }
        return (
            <ux-button
                href={this.link}
                class={`link-button link-button--${breakpoint}`}
                icon="arrow-right"
                icon-placement="end"
                target="_blank"
            >
                {this.linkLabel}
            </ux-button>
        );
    }

    render() {
        return (
            <Host
                class={{
                    "is-mobile": this.isMobile,
                    "is-opened": this.isOpened,
                    "is-hovered": this.isHovered,
                }}
            >
                <div class="tile-content">
                    <div class="media-container">
                        {this.video && (
                            <video
                                ref={(el) => (this.videoElement = el)}
                                poster={this.videoPoster}
                                loop
                                muted
                                playsinline
                                preload="auto"
                                autoplay={this.isVideoPlaying}
                            >
                                <source
                                    src={this.video}
                                    type={this.videoType}
                                />
                            </video>
                        )}
                        {!this.video && <img src={this.videoPoster} alt="" />}
                    </div>
                    <button
                        class="button-area"
                        aria-label={this.openButtonLabel}
                        onClick={() => {
                            if (this.isOpened) {
                                return;
                            }
                            this.isOpened = true;
                        }}
                        onMouseEnter={() => (this.isHovered = true)}
                        onMouseLeave={() => (this.isHovered = false)}
                    >
                        <div class="button-area-content">
                            <div class="tile-title-container">
                                <slot name="tile-title" />
                            </div>
                            <div class="tile-button-container">
                                <div class="tile-button">
                                    <ux-icon name="plus" />
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
                <ux-modal
                    opened={this.isOpened}
                    onUxModalClose={() => {
                        this.isOpened = false;
                        console.log("pause", this.modalVideoElement);
                        this.modalVideoElement?.pause();
                    }}
                    closeOnBackdropClick
                >
                    {this.renderModalMedia("desktop")}
                    <ux-modal-content>
                        <slot name="modal-title" />
                        {this.renderModalMedia("mobile")}
                        <div class="modal-challenge-container">
                            <h3 class="modal-section-headline">
                                {this.challengeHeadline}
                            </h3>
                            <slot name="modal-challenge" />
                        </div>
                        <div class="modal-solution-container">
                            <h3 class="modal-section-headline">
                                {this.solutionHeadline}
                            </h3>
                            <slot name="modal-solution" />
                        </div>
                        {this.renderLinkButton("desktop")}
                    </ux-modal-content>
                    {this.link !== "" && (
                        <ux-modal-footer>
                            {this.renderLinkButton("mobile")}
                        </ux-modal-footer>
                    )}
                </ux-modal>
            </Host>
        );
    }
}
